/**
 * Confetti — a decorative, finite celebration burst.
 *
 * Two "cannons" in the lower half of the left and right edges of the nearest
 * positioned ancestor fire pieces toward the centre at the same time. Each
 * piece fades in, arcs up (apex around the upper third of the container),
 * falls back down and fades out. The whole burst is over after
 * `CONFETTI_LIFETIME_MS` (4 s): the component then renders nothing, so a host
 * can leave it mounted and still end up with a clean screen. Mount it again
 * (new key / re-open) for a new burst.
 *
 * Decorative only: `aria-hidden`, `pointer-events: none`, and exactly four
 * fixed brand colours (see Confetti.css). Under `prefers-reduced-motion` the
 * pieces do not fly; they sit still around their apex and only fade in and
 * out. Seeded, so a given `seed` always yields the same burst and a re-render
 * never reshuffles it.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';

import './Confetti.css';

export const CONFETTI_COLORS = ['blue', 'pink', 'orange', 'purple'] as const;
export type ConfettiColor = (typeof CONFETTI_COLORS)[number];

export const CONFETTI_SIDES = ['left', 'right'] as const;
export type ConfettiSide = (typeof CONFETTI_SIDES)[number];

export const CONFETTI_DEFAULT_COUNT = 150;

/** The whole burst, first launch to last piece gone, in milliseconds. */
export const CONFETTI_LIFETIME_MS = 4000;

/** The last piece leaves its cannon this long after the first, in seconds. */
const LAUNCH_WINDOW_S = 0.5;
const FLIGHT_MIN_S = 2.8;
/** Longest flight: launch window + flight never exceeds the lifetime. */
const FLIGHT_MAX_S = CONFETTI_LIFETIME_MS / 1000 - LAUNCH_WINDOW_S;

export type ConfettiProps = {
  /** Number of pieces, split evenly between the two cannons. */
  count?: number;
  /** PRNG seed; same seed, same burst. */
  seed?: number;
  className?: string;
};

/** mulberry32 — tiny seeded PRNG so a given seed always yields the same burst. */
function createRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type Piece = {
  color: ConfettiColor;
  side: ConfettiSide;
  style: CSSProperties;
};

/**
 * Geometry is written as plain numbers in percent of the container
 * (`--spark-confetti-origin-y`, `--spark-confetti-rise`, `--spark-confetti-dx`);
 * Confetti.css multiplies them by its container-relative units, so the burst
 * scales with whatever it is mounted in.
 */
function buildPieces(count: number, seed: number): Piece[] {
  const random = createRandom(seed);
  const between = (min: number, max: number) => min + random() * (max - min);
  return Array.from({ length: count }, (_, index) => {
    const side = CONFETTI_SIDES[index % CONFETTI_SIDES.length];
    // Lower half of the edge …
    const originY = between(55, 95);
    // … up to an apex around the upper third …
    const apexY = between(12, 40);
    // … while travelling this far toward the centre (percent of the width).
    const reach = between(28, 60);
    return {
      // Walk the palette per cannon, so both sides fire every colour.
      color: CONFETTI_COLORS[Math.floor(index / CONFETTI_SIDES.length) % CONFETTI_COLORS.length],
      side,
      style: {
        '--spark-confetti-origin-y': originY.toFixed(2),
        '--spark-confetti-rise': (originY - apexY).toFixed(2),
        '--spark-confetti-dx': (side === 'left' ? reach : -reach).toFixed(2),
        '--spark-confetti-width': `${Math.round(between(6, 11))}px`,
        '--spark-confetti-height': `${Math.round(between(10, 18))}px`,
        '--spark-confetti-rotate': `${Math.round(between(0, 360))}deg`,
        '--spark-confetti-spin': `${Math.round(between(-720, 720))}deg`,
        '--spark-confetti-duration': `${between(FLIGHT_MIN_S, FLIGHT_MAX_S).toFixed(2)}s`,
        '--spark-confetti-delay': `${between(0, LAUNCH_WINDOW_S).toFixed(2)}s`,
      } as CSSProperties,
    };
  });
}

export function Confetti({ count = CONFETTI_DEFAULT_COUNT, seed = 1, className = '' }: ConfettiProps) {
  const pieces = useMemo(() => buildPieces(Math.max(0, Math.floor(count)), seed), [count, seed]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDone(true), CONFETTI_LIFETIME_MS);
    return () => window.clearTimeout(timer);
  }, []);

  if (done) return null;

  return (
    <div
      className={['spark-confetti', className].filter(Boolean).join(' ')}
      aria-hidden="true"
      data-testid="spark-confetti"
    >
      {pieces.map((piece, index) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          className={`spark-confetti-piece spark-confetti-piece--${piece.side} spark-confetti-piece--${piece.color}`}
          style={piece.style}
        >
          <span className="spark-confetti-piece-body" />
        </span>
      ))}
    </div>
  );
}
